import { FC, useCallback, useMemo, useState } from 'react';
import { StarIcon } from '@features/ui/icons';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Checkbox,
  RadioGroup,
  RadioGroupItem,
  RadixTable,
  TColumn,
} from '@features/ui';
import { AccountStatusCell } from '@features/admin/components/AccountStatusCell';
import {
  accountActivityCheckbox,
  accountActivityRadio,
} from '@features/admin/users/usersData.ts';
import {
  AccountActivityActiveStatus,
  AccountActivityDisplay,
  CloseUser,
  CrRefundFormData,
  EditBalanceFormData,
  LeelooAccounts,
  PayoutRequestFormData,
  UserDetailQuery,
  UserProfile,
} from '@src/types/users.ts';
import { formatCurrency, formatDate } from '@lib/utils.ts';
import { AddCrRefundPopup } from '@features/admin/users/AddCrRefundPopup.tsx';
import {
  assignAccount,
  bulkLeelooCloseAccount,
  creditUser,
  editUserBalance,
  leelooCloseAccount,
  reEnableEvalToPa,
} from '@src/services/users.ts';
import { requestPayoutManual } from '@src/services/payout.ts';
import { toast } from 'react-toastify';
import { AddPayoutRequestPopup } from '@features/admin/users/AddPayoutRequestPopup.tsx';
import { EditAccountBalancePopup } from '@features/admin/users/EditAccountPopup.tsx';
import { ConfirmClosePopup } from '@features/admin/users/ConfirmClosePopup.tsx';
import { AccountChartsDialog } from '@features/admin/users/AccountChartsDialog.tsx';
import { canAccess } from '@src/utils/auth';
import { UserRolePermission } from '@src/types/settings';
import { useAuthStore } from '@src/stores/authStore';

interface AccountActivityProps {
  user: UserProfile | null;
  filteredAccounts: LeelooAccounts[];
  refreshUser: (params?: UserDetailQuery) => Promise<void>;
}
export const AccountActivity: FC<AccountActivityProps> = ({
  user,
  refreshUser,
  filteredAccounts,
}) => {
  const { auth } = useAuthStore();
  const [closeAccount, setCloseAccount] = useState(false);
  const [editAccountBalance, setEditAccountBalance] = useState(false);
  const [crRefundOpen, setCrRefundOpen] = useState(false);
  const [payoutRequestOpen, setPayoutRequestOpen] = useState(false);
  const [displays, setDisplays] = useState<AccountActivityDisplay[]>([
    AccountActivityDisplay.ShowEvals,
    AccountActivityDisplay.ShowPas,
  ]);
  const [accountActivityRadioSelected, setAccountActivityRadioSelected] =
    useState<string>(AccountActivityActiveStatus.LiveOnly);
  const [filterAccountSelected, setFilterAccountSelected] =
    useState<LeelooAccounts | null>(null);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedAccounts, setSelectedAccounts] = useState<LeelooAccounts[]>(
    []
  );
  const [chartsOpen, setChartsOpen] = useState(false);
  const [chartAccount, setChartAccount] = useState<LeelooAccounts | null>(null);

  const { t } = useTranslation();
  const accounts = useMemo(() => filteredAccounts, [filteredAccounts]);

  const handleSelectAll = useCallback(() => {
    setSelectAll(!selectAll);
    if (!selectAll) {
      setSelectedAccounts(accounts);
    } else {
      setSelectedAccounts([]);
    }
  }, [selectAll, accounts]);

  const handleSelectAccount = useCallback((account: LeelooAccounts) => {
    setSelectedAccounts((prev) => {
      if (prev.some((item) => item.id === account.id)) {
        return prev.filter((item) => item.id !== account.id);
      } else {
        return [...prev, account];
      }
    });
  }, []);

  const handleChangeDisplay = useCallback(
    async (val: AccountActivityDisplay) => {
      const isActive = displays.includes(val);

      const updatedDisplays = isActive
        ? displays.filter((item) => item !== val)
        : [...displays, val];

      setDisplays(updatedDisplays);

      const displayParams = updatedDisplays.reduce<Record<string, boolean>>(
        (acc, key) => {
          acc[key] = true;

          return acc;
        },
        {}
      );

      if (
        accountActivityRadioSelected === AccountActivityActiveStatus.LiveOnly
      ) {
        displayParams.live_only = true;
      }

      await refreshUser(displayParams);
    },
    [accountActivityRadioSelected, displays, refreshUser]
  );

  const handleChangeAccountActivityRadioSelected = useCallback(
    async (val: AccountActivityActiveStatus) => {
      setAccountActivityRadioSelected(val);

      const displayParams = displays.reduce<Record<string, boolean>>(
        (acc, key) => {
          acc[key] = true;

          return acc;
        },
        {}
      );

      if (val === AccountActivityActiveStatus.LiveOnly) {
        displayParams.live_only = true;
      }

      await refreshUser(displayParams);
    },
    [displays, refreshUser]
  );

  const handleCloseAccount = useCallback((account?: LeelooAccounts) => {
    if (!account) return;
    setCloseAccount(true);
    setFilterAccountSelected(account);
  }, []);

  const handleBulkCloseAccount = useCallback(async () => {
    if (!user?.id) return;
    try {
      const displayParams = displays.reduce<Record<string, boolean>>(
        (acc, key) => {
          acc[key] = true;

          return acc;
        },
        {}
      );

      if (
        accountActivityRadioSelected === AccountActivityActiveStatus.LiveOnly
      ) {
        displayParams.live_only = true;
      }
      const accounts = selectedAccounts.map((item) => ({
        leeloo_user_id: user.id,
        leeloo_account_id: item.id,
      }));

      const res = await bulkLeelooCloseAccount({
        id: user.id,
        accounts: accounts as CloseUser[],
      });

      await refreshUser(displayParams);
      setSelectAll(false);
      setSelectedAccounts([]);
      toast.success(res?.data?.message || 'Accounts closed successfully.');
    } catch {
      toast('Accounts close failed. Please try again.');
    }
  }, [
    accountActivityRadioSelected,
    displays,
    refreshUser,
    selectedAccounts,
    user?.id,
  ]);

  const handleEditAccountBalance = useCallback((account?: LeelooAccounts) => {
    if (!account) return;
    setEditAccountBalance(true);
    setFilterAccountSelected(account);
  }, []);

  const handleSelectCrRefund = useCallback((account?: LeelooAccounts) => {
    if (!account) return;
    setCrRefundOpen(true);
    setFilterAccountSelected(account);
  }, []);

  const handleSelectPayoutRequest = useCallback((account?: LeelooAccounts) => {
    if (!account) return;
    setPayoutRequestOpen(true);
    setFilterAccountSelected(account);
  }, []);

  const handleOpenCharts = useCallback((account?: LeelooAccounts) => {
    if (!account?.id) return;
    setChartAccount(account);
    setChartsOpen(true);
  }, []);

  const handleCloseAccountConfirm = useCallback(async () => {
    if (!user?.id || !filterAccountSelected?.id) return;

    try {
      const displayParams = displays.reduce<Record<string, boolean>>(
        (acc, key) => {
          acc[key] = true;

          return acc;
        },
        {}
      );

      if (
        accountActivityRadioSelected === AccountActivityActiveStatus.LiveOnly
      ) {
        displayParams.live_only = true;
      }

      const res = await leelooCloseAccount({
        id: user.id,
        accountId: filterAccountSelected.id,
      });

      setSelectedAccounts((prev) =>
        prev.filter((account) => account.id !== filterAccountSelected.id)
      );
      await refreshUser(displayParams);
      setFilterAccountSelected(null);
      setEditAccountBalance(false);
      toast.success(res?.data?.message || 'Account closed successfully.');
    } catch {
      toast.error('Account close failed. Please try again.');
    }
  }, [
    accountActivityRadioSelected,
    displays,
    filterAccountSelected?.id,
    refreshUser,
    user?.id,
  ]);

  const handleAssignAccount = useCallback(
    async (id?: number) => {
      if (!user?.id || !id) return;
      try {
        const displayParams = displays.reduce<Record<string, boolean>>(
          (acc, key) => {
            acc[key] = true;

            return acc;
          },
          {}
        );

        if (
          accountActivityRadioSelected === AccountActivityActiveStatus.LiveOnly
        ) {
          displayParams.live_only = true;
        }
        const res = await assignAccount(user.id, id);

        await refreshUser(displayParams);

        toast.success(res?.data?.message || 'Account assigned successfully.');
      } catch {
        toast.error('Account assign failed. Please try again.');
      }
    },
    [accountActivityRadioSelected, displays, refreshUser, user?.id]
  );

  const handleReEnableEvalToPa = useCallback(
    async (id?: number) => {
      if (!user?.id || !id) return;
      try {
        const displayParams = displays.reduce<Record<string, boolean>>(
          (acc, key) => {
            acc[key] = true;

            return acc;
          },
          {}
        );

        if (
          accountActivityRadioSelected === AccountActivityActiveStatus.LiveOnly
        ) {
          displayParams.live_only = true;
        }
        const res = await reEnableEvalToPa(user.id, id);

        await refreshUser(displayParams);

        toast.success(res?.data?.message || 'Account re-enabled successfully.');
      } catch {
        toast.error('Account re-enable failed. Please try again.');
      }
    },
    [accountActivityRadioSelected, displays, refreshUser, user?.id]
  );

  const handleEditUserBalance = useCallback(
    async (data: EditBalanceFormData) => {
      if (!user?.id || !filterAccountSelected?.id) return;
      const displayParams = displays.reduce<Record<string, boolean>>(
        (acc, key) => {
          acc[key] = true;

          return acc;
        },
        {}
      );

      if (
        accountActivityRadioSelected === AccountActivityActiveStatus.LiveOnly
      ) {
        displayParams.live_only = true;
      }
      await editUserBalance(user.id, filterAccountSelected.id, data);
      await refreshUser(displayParams);
      setFilterAccountSelected(null);
      setEditAccountBalance(false);
    },
    [
      accountActivityRadioSelected,
      displays,
      filterAccountSelected?.id,
      refreshUser,
      user?.id,
    ]
  );

  const handleApplyCreditUser = useCallback(
    async (data: CrRefundFormData) => {
      if (!user?.id || !filterAccountSelected?.id) return;
      const displayParams = displays.reduce<Record<string, boolean>>(
        (acc, key) => {
          acc[key] = true;

          return acc;
        },
        {}
      );

      if (
        accountActivityRadioSelected === AccountActivityActiveStatus.LiveOnly
      ) {
        displayParams.live_only = true;
      }
      await creditUser(user.id, filterAccountSelected.id, data);
      await refreshUser(displayParams);
      setFilterAccountSelected(null);
      setCrRefundOpen(false);
    },
    [
      accountActivityRadioSelected,
      displays,
      filterAccountSelected?.id,
      refreshUser,
      user?.id,
    ]
  );

  const handleSavePayoutRequest = useCallback(
    async (data: PayoutRequestFormData) => {
      if (!user?.id || !filterAccountSelected?.id) return;

      const displayParams = displays.reduce<Record<string, boolean>>(
        (acc, key) => {
          acc[key] = true;

          return acc;
        },
        {}
      );

      if (
        accountActivityRadioSelected === AccountActivityActiveStatus.LiveOnly
      ) {
        displayParams.live_only = true;
      }

      try {
        await requestPayoutManual({
          account_id: filterAccountSelected.id,
          leeloo_user_id: user.id,
          payout_amount: data.payout_amount,
          // backend field name is `rithmic_deduction` (optional)
          rithmic_deduction:
            data.rithmic_deduction_amount ?? data.payout_amount,
          payment_method: (data as any).payment_methods ?? 'unknown',
          override_logic: data.override ?? undefined,
          notes: data.note ?? undefined,
        });

        toast.success('Payout request submitted');
      } catch (err) {
        console.error('requestPayoutManual failed', err);
        toast.error('Unable to submit payout request. Please try again later.');
      }

      setFilterAccountSelected(null);
      setPayoutRequestOpen(false);
      await refreshUser(displayParams);
    },
    [
      accountActivityRadioSelected,
      displays,
      filterAccountSelected?.id,
      refreshUser,
      user?.id,
    ]
  );

  const columns = useMemo(
    (): TColumn<LeelooAccounts>[] => [
      {
        render: (item) => (
          <div className={`flex items-center gap-x-2.5`}>
            <Checkbox
              checked={selectedAccounts.some(
                (selected) => selected.id === item?.id
              )}
              className={'border-[white] border-[1px]'}
              id="user"
              onCheckedChange={() => item && handleSelectAccount(item)}
            />
            <div className={'flex flex-col gap-1'}>
              <p className={'text-blue'}>{user?.username ?? ''}</p>
              <p>
                {user?.qualifying_account_name
                  ? user?.is_pro
                    ? 'From'
                    : 'To'
                  : ''}{' '}
                {user?.qualifying_account_name ?? ''}
              </p>
            </div>
          </div>
        ),
        title: (
          <div className={'flex items-center gap-x-2.5'}>
            <Checkbox
              checked={selectAll}
              className={'border-[white] border-[1px]'}
              id="user"
              onCheckedChange={handleSelectAll}
            />
            <p>User</p>
          </div>
        ),
      },
      {
        title: 'Account ID/ Package',
        render: (item) => (
          <div className={'flex flex-col gap-1'}>
            <p className={'text-blue'}>{item?.rithmic_account_name ?? ''}</p>
            <p>{item?.product?.title ?? ''}</p>
          </div>
        ),
      },
      {
        title: 'Trading',
        render: (item) => (
          <div className={'pr-1 min-w-[80px]'}>
            <button
              className={`relative flex h-[30px] min-w-[30px] px-1 bg-[#B6D72C] rounded-[2px] items-center w-fit justify-center hover:opacity-90`}
              onClick={() => handleOpenCharts(item)}
            >
              {Number(item?.extra_trading_days ?? 0) +
                Number(item?.trading_days ?? 0) >
                Number(item?.trading_days ?? 0) && (
                <StarIcon
                  className="absolute -top-1 -right-1"
                  color="#000000"
                  size={10}
                  title="Has extra trading days"
                />
              )}
              {Number(item?.trading_days) + Number(item?.extra_trading_days)}
            </button>
          </div>
        ),
      },
      {
        title: 'Balance',
        render: (item) => (
          <div className={'min-w-[80px]'}>
            <p>${formatCurrency(Number(item?.account_balance ?? 0), 2)}</p>
            <p>
              {item?.account_balance_date
                ? formatDate(item.account_balance_date)
                : ''}
            </p>
          </div>
        ),
      },
      {
        title: 'Status',
        render: (item) => (
          <AccountStatusCell
            qualifyingAccountName={item?.qualifying_account_name}
            qualifyingStatus={item?.qualifying_status}
            qualifyingStatusDetail={item?.qualifying_status_detail}
          />
        ),
      },
      // {
      //   title: '',
      //   render: () => (
      //     <button className={'cursor-pointer'}>
      //       <PauseIcon />
      //     </button>
      //   ),
      // },
      {
        title: 'Action',
        render: (item) => {
          const qualifyExpireDate = item?.qualify_expire
            ? new Date(item.qualify_expire)
            : null;
          const isQualifyExpireValid =
            qualifyExpireDate && !Number.isNaN(qualifyExpireDate.getTime());

          const canReEnableEvalToPa =
            item?.qualifying_status === 'expired' &&
            isQualifyExpireValid &&
            qualifyExpireDate.getTime() <= Date.now();

          return (
            <div className={'flex items-center gap-x-3.5'}>
              {canReEnableEvalToPa && (
                <Button
                  className="bg-[#007bff] text-white hover:bg-[#0069d9]"
                  size={'sm'}
                  onClick={() => handleReEnableEvalToPa(item?.id)}
                >
                  Re-enable Eval &gt; PA
                </Button>
              )}
              {canAccess(
                UserRolePermission.CLOSE_ACCOUNT,
                auth?.user?.permissions
              ) && (
                <Button
                  className={'bg-[#C13839]'}
                  size={'sm'}
                  onClick={() => handleCloseAccount(item)}
                >
                  Close
                </Button>
              )}
              {canAccess(
                UserRolePermission.ASSIGN_ACCOUNT,
                auth?.user?.permissions
              ) && (
                <Button
                  size={'sm'}
                  variant={'secondary'}
                  onClick={() => handleAssignAccount(item?.id)}
                >
                  Assign
                </Button>
              )}
              {canAccess(
                UserRolePermission.EDIT_ACCOUNT,
                auth?.user?.permissions
              ) && (
                <Button
                  size={'sm'}
                  variant={'black'}
                  onClick={() => handleEditAccountBalance(item)}
                >
                  Edit
                </Button>
              )}
              {canAccess(
                UserRolePermission.CREDIT_REFUND_ACCOUNT,
                auth?.user?.permissions
              ) && (
                <Button
                  className={'bg-[#939393] hover:bg-[#939393CC]'}
                  size={'sm'}
                  variant={'black'}
                  onClick={() => handleSelectCrRefund(item)}
                >
                  CR / Refund
                </Button>
              )}
              {item?.is_pro && (
                <Button
                  size={'sm'}
                  variant={'lime'}
                  onClick={() => handleSelectPayoutRequest(item)}
                >
                  Payout Request
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    [
      handleAssignAccount,
      handleCloseAccount,
      handleEditAccountBalance,
      handleOpenCharts,
      handleSelectAccount,
      handleSelectAll,
      handleSelectCrRefund,
      handleSelectPayoutRequest,
      selectAll,
      selectedAccounts,
      user?.is_pro,
      user?.qualifying_account_name,
      user?.username,
    ]
  );

  return (
    <>
      <div className={'lg:absolute lg:right-3 lg:top-0 lg:mb-0 mb-4 flex'}>
        <div className={'flex gap-[13px] text-sm mr-[32px]'}>
          <p>{t('users.traderPaid')}</p>
          <p className={'text-blue'}>${user?.trader_paid}</p>
        </div>
        <div className={'flex gap-[13px] text-sm'}>
          <p>{t('users.leelooPaid')}</p>
          <p className={'text-blue'}>${user?.leeloo_paid}</p>
        </div>
      </div>
      <div className={'flex flex-col gap-10'}>
        <div className={'text-sm flex lg:flex-row flex-col lg:gap-8 gap-4'}>
          <div className={'flex gap-2.5 items-center'}>
            <p className={'mr-1'}>{t('users.display')}</p>
            {accountActivityCheckbox.map((item) => (
              <div key={item.value} className={'flex gap-3 items-center'}>
                <Checkbox
                  checked={displays.includes(
                    item.value as AccountActivityDisplay
                  )}
                  className={'size-5'}
                  id={`checkbox-${item.value}`}
                  onCheckedChange={() =>
                    handleChangeDisplay(item.value as AccountActivityDisplay)
                  }
                />
                <p>{item.label}</p>
              </div>
            ))}
          </div>
          <div className={'flex gap-3.5 items-center'}>
            <p>{t('users.accountStatus')}</p>
            <RadioGroup
              className={'flex item-center gap-x-2.5'}
              defaultValue={accountActivityRadioSelected}
              onValueChange={handleChangeAccountActivityRadioSelected}
            >
              {accountActivityRadio.map((item) => {
                return (
                  <div key={item.value} className={'flex items-center gap-x-3'}>
                    <RadioGroupItem value={item.value} />
                    <p>{item.label}</p>
                  </div>
                );
              })}
            </RadioGroup>
          </div>
        </div>
        {selectedAccounts.length > 0 && (
          <Button
            className={'bg-[#C13839] w-fit'}
            size={'sm'}
            onClick={handleBulkCloseAccount}
          >
            Close Selected Accounts
          </Button>
        )}
        <RadixTable columns={columns} data={accounts} />
      </div>
      <ConfirmClosePopup
        open={closeAccount}
        onConfirm={handleCloseAccountConfirm}
        onOpenChange={setCloseAccount}
      />
      <EditAccountBalancePopup
        account={filterAccountSelected}
        open={editAccountBalance}
        onOpenChange={setEditAccountBalance}
        onSave={handleEditUserBalance}
      />
      <AddCrRefundPopup
        account={filterAccountSelected}
        open={crRefundOpen}
        onOpenChange={setCrRefundOpen}
        onSave={handleApplyCreditUser}
      />
      <AddPayoutRequestPopup
        account={filterAccountSelected}
        open={payoutRequestOpen}
        user={user}
        onOpenChange={setPayoutRequestOpen}
        onSave={handleSavePayoutRequest}
      />

      {/* Charts Popup */}
      <AccountChartsDialog
        accountId={chartAccount?.id ?? null}
        accountName={chartAccount?.rithmic_account_name ?? ''}
        open={chartsOpen}
        onOpenChange={setChartsOpen}
      />
    </>
  );
};
